import type { ExpiringAgreementModel } from '@kk/api/modules/leasing';
import { createContext, type ReactNode, useContext, useState } from 'react';
import type { z } from 'zod';
import type { Action } from '@/routes/TEMPORARY_TYPES';
import type { AyvensExtendInfo } from './schemas/ayvensExtendInfoSchema';
import type { ayvensPickupInfoSchema } from './schemas/ayvensPickupInfoSchema';
import type { buyInfoSchema } from './schemas/buyInfoSchema';
import type { extendInfoSchema } from './schemas/extendInfoSchema';
import type { sellInfoSchema } from './schemas/sellInfoSchema';

export enum AgreementFlowType {
  Leasing = 'leasing',
  Ayvens = 'ayvens',
}

type LeasingFlowStep = 'select' | 'addinfo' | 'confirm';
type AyvensFlowStep = 'select' | 'addinfo' | 'confirm';
type FlowStep = LeasingFlowStep | AyvensFlowStep;

type BuyInfo = z.infer<typeof buyInfoSchema>;
type SellInfo = z.infer<typeof sellInfoSchema>;
type ExtendInfo = z.infer<typeof extendInfoSchema>;
type AyvensPickupInfo = z.infer<typeof ayvensPickupInfoSchema>;

type FlowState = {
  paramA: string | null;
  paramB: string | null;
  paramP: string | null;
  setParamA: (value: string | null) => void;
  setParamB: (value: string | null) => void;
  setParamP: (value: string | null) => void;
  agreements: ExpiringAgreementModel[];
  updateAgreements: (agreements: ExpiringAgreementModel[]) => void;
  buyInfo: BuyInfo | null;
  setBuyInfo: (info: BuyInfo) => void;
  sellInfo: SellInfo | null;
  setSellInfo: (info: SellInfo) => void;
  extendInfo: ExtendInfo | null;
  setExtendInfo: (info: ExtendInfo) => void;
  ayvensPickupInfo: AyvensPickupInfo | null;
  setAyvensPickupInfo: (info: AyvensPickupInfo) => void;
  ayvensExtendInfo: AyvensExtendInfo | null;
  setAyvensExtendInfo: (info: AyvensExtendInfo) => void;
  action: Action | null;
  setAction: (action: Action | null) => void;
  step: FlowStep;
  nextStep: () => void;
  reset: () => void;
};

const ExpiringFlowContext = createContext<FlowState | undefined>(undefined);

export const ExpiringFlowProvider = ({ children }: { children: ReactNode }) => {
  const [paramA, setParamA] = useState<string | null>(null);
  const [paramB, setParamB] = useState<string | null>(null);
  const [paramP, setParamP] = useState<string | null>(null);
  const [agreements, setAgreements] = useState<ExpiringAgreementModel[]>([]);
  const [step, setStep] = useState<FlowStep>('select');
  const [action, setAction] = useState<Action | null>(null);
  const [buyInfo, setBuyInfo] = useState<BuyInfo | null>(null);
  const [sellInfo, setSellInfo] = useState<SellInfo | null>(null);
  const [extendInfo, setExtendInfo] = useState<ExtendInfo | null>(null);
  const [ayvensPickupInfo, setAyvensPickupInfo] =
    useState<AyvensPickupInfo | null>(null);
  const [ayvensExtendInfo, setAyvensExtendInfo] =
    useState<AyvensExtendInfo | null>(null);

  const updateAgreements = (agreements: ExpiringAgreementModel[]) => {
    setAgreements(agreements);
  };

  const nextStep = () => {
    setStep((prev) => {
      if (prev === 'select') return 'addinfo';
      if (prev === 'addinfo') return 'confirm';
      return prev;
    });
  };

  const reset = () => {
    setAgreements([]);
    setStep('select');
    setAction(null);
    setBuyInfo(null);
    setSellInfo(null);
    setExtendInfo(null);
    setAyvensPickupInfo(null);
    setAyvensExtendInfo(null);
  };

  return (
    <ExpiringFlowContext.Provider
      value={{
        paramA,
        paramB,
        paramP,
        setParamA,
        setParamB,
        setParamP,
        agreements,
        updateAgreements,
        buyInfo,
        setBuyInfo,
        sellInfo,
        setSellInfo,
        extendInfo,
        setExtendInfo,
        ayvensPickupInfo,
        setAyvensPickupInfo,
        ayvensExtendInfo,
        setAyvensExtendInfo,
        action,
        setAction,
        step,
        nextStep,
        reset,
      }}
    >
      {children}
    </ExpiringFlowContext.Provider>
  );
};

export const useExpiringFlow = () => {
  const ctx = useContext(ExpiringFlowContext);
  if (!ctx) throw new Error('ExpiringFlow must be used within provider');
  return ctx;
};
